import clientTraceContract from "./client-trace-contract.json";

const CLIENT_TRACE_SESSION_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/;

export type T_ClientTraceActivation =
  | {
      status: "disabled";
      storageAction: "clear" | "none";
    }
  | {
      sessionId: string;
      status: "enabled";
      storageAction: "none" | "persist";
    };

type T_ResolveClientTraceActivationArgs = {
  appEnvironment: string;
  search: string;
  storedSession: string | null;
};

export const CLIENT_TRACE_COLLECTOR_PATH = clientTraceContract.collectorPath;
export const CLIENT_TRACE_SESSION_STORAGE_KEY =
  clientTraceContract.sessionStorageKey;

export function isValidClientTraceSession(value: string): boolean {
  return CLIENT_TRACE_SESSION_PATTERN.test(value);
}

/**
 * Local browser traces are explicitly session-scoped. A query parameter starts
 * a trace, sessionStorage keeps it through reload/navigation, and `off` ends it.
 */
export function resolveClientTraceActivation({
  appEnvironment,
  search,
  storedSession,
}: T_ResolveClientTraceActivationArgs): T_ClientTraceActivation {
  if (appEnvironment !== "local") {
    return { status: "disabled", storageAction: "clear" };
  }

  const requestedSession = new URLSearchParams(search).get(
    clientTraceContract.queryParameter,
  );
  if (requestedSession !== null) {
    if (requestedSession === "off") {
      return { status: "disabled", storageAction: "clear" };
    }
    if (!isValidClientTraceSession(requestedSession)) {
      return { status: "disabled", storageAction: "clear" };
    }

    return {
      sessionId: requestedSession,
      status: "enabled",
      storageAction: "persist",
    };
  }

  if (storedSession && isValidClientTraceSession(storedSession)) {
    return {
      sessionId: storedSession,
      status: "enabled",
      storageAction: "none",
    };
  }

  return {
    status: "disabled",
    storageAction: storedSession ? "clear" : "none",
  };
}
